import { useEffect, useMemo, useRef, useState } from 'react'
import { messageOf } from '../../lib/errors'
import {
  isCommandKey,
  setStockBatch,
  stockRefusal,
  suggestStock,
  type ManagedItem,
  type ManagedVariant,
  type RoomInventoryRow,
  type StockRoom,
} from '../../lib/shop'

/**
 * ADD ITEM STOCK — the room, then the item, then every combination at once.
 *
 * David, 15 Sep: "ask for the store first / suggest recent store items, or items
 * recently out of stock or low stock for that store / make the item picker
 * ctrl+K searchable / when an item is selected, load all the variations and let
 * them enter quantities for each on one commit."
 *
 * ── THE ROOM IS FIRST BECAUSE NOTHING ELSE IS TRUE WITHOUT IT ───────────────
 *
 * 074's first cut asked for the item first, which read naturally and was wrong:
 * every number on this form — what is there now, what is promised, what is low
 * — is a property of a variant AND a room, so before a room is named the grid
 * can only be blank or lying. It is also the question with the fewest answers
 * (the rooms you run) and the one already answered most of the time.
 *
 * That ordering is what makes the suggestions possible at all, and they are the
 * real payoff: the commonest reason to open this form is that something ran out
 * HERE, and a search box cannot know that.
 *
 * ── THE SUGGESTIONS COME FROM THE SHELF, NOT FROM A SECOND DEFINITION ───────
 *
 * `suggestStock` reads the same `stock_room_inventory` rows the table renders.
 * A separate "what should I restock" query would be a second copy of 074's
 * reorder rule, and the first thing the two would disagree about is what "low"
 * means — on the one screen where that has to be one answer.
 *
 * ── ONE COMMIT, AND IT IS STILL A COUNT ─────────────────────────────────────
 *
 * Every active combination gets a field, prefilled with what the database
 * currently believes, and one press sends the changed ones. 065's no-delta rule
 * survives intact and this is the screen that argues with it hardest: a box of
 * ten arriving against four on the shelf means typing 14, not +10. The
 * counter-argument is real — a receipt is a different act from a count, and two
 * receipts of ten typed as one absolute total lose ten units — and it is filed
 * as REC-41 rather than settled here, because a receipt wants its own record.
 *
 * The current figure sits beside every field so the arithmetic is visible
 * rather than remembered.
 */
export function AddStock({
  items,
  rooms,
  roomId,
  onPickRoom,
  itemId,
  onPickItem,
  rows,
  loading,
  error,
  onSaved,
  onCancel,
  onError,
}: {
  /** Every item the caller may edit, with its variants and axes. */
  items: ManagedItem[]
  rooms: StockRoom[]
  /** Chosen in step one. Null until then, and nothing below it renders. */
  roomId: string | null
  onPickRoom: (roomId: string | null) => void
  /**
   * Chosen in step two, and CONTROLLED by the route rather than held here —
   * the same shape `ShopItems` uses for its open panel, and for the same
   * reason: a grid that can only be reached by clicking is a grid no assertion
   * can render, so every check written against it passes whether the contents
   * are right or not (062's lesson, third time in this screen).
   */
  itemId: string
  onPickItem: (itemId: string) => void
  /**
   * That room's shelf. Loaded by the ROUTE, not here — effects do not run under
   * `renderToString`, so a component that fetches its own contents is one whose
   * grid, suggestions and prefilled counts the suite can only ever see empty
   * (062's lesson, and the same reason RoomStock takes props).
   */
  rows: RoomInventoryRow[]
  loading: boolean
  error: string | null
  onSaved: (written: number) => void
  onCancel: () => void
  onError: (m: string | null) => void
}) {
  const mine = useMemo(() => rooms.filter((r) => r.iManage && r.isActive), [rooms])
  const [query, setQuery] = useState('')
  const [counts, setCounts] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const searchRef = useRef<HTMLInputElement>(null)

  // Ctrl+K / Cmd+K. `preventDefault` is mandatory — both Chrome and Firefox
  // take this key for the address bar — and is only defensible because this
  // modal already owns the page. See isCommandKey.
  useEffect(() => {
    if (!roomId || itemId) return
    function onKey(e: KeyboardEvent) {
      if (!isCommandKey(e)) return
      e.preventDefault()
      searchRef.current?.focus()
      searchRef.current?.select()
    }
    document.addEventListener('keydown', onKey)
    // Focused on arrival too: somebody who has just chosen a room is about to
    // name an item, and a shortcut is for the second time, not the first.
    searchRef.current?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [roomId, itemId])

  const item = items.find((i) => i.itemId === itemId) ?? null
  const room = mine.find((r) => r.id === roomId) ?? null

  // Only what is on the shelf FOR THIS ITEM. Keyed by variant so a grid row can
  // find its own current numbers without a scan per render.
  const shelf = useMemo(() => {
    const m = new Map<string, RoomInventoryRow>()
    for (const r of rows) m.set(r.variantId, r)
    return m
  }, [rows])

  const groups = useMemo(() => suggestStock(rows), [rows])

  // Active combinations only. A retired one may still hold stock and is still
  // on the room's shelf, but putting NEW boxes against something nobody may buy
  // is not a thing to offer — correcting that count belongs on the shelf.
  const variants: ManagedVariant[] = useMemo(
    () => (item?.variants ?? []).filter((v) => v.isActive),
    [item],
  )

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    const pool = items.filter((i) => i.isActive)
    if (!q) return pool.slice(0, 8)
    return pool
      .filter((i) => i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q))
      .slice(0, 8)
  }, [items, query])

  function shown(v: ManagedVariant): string {
    const typed = counts[v.variantId]
    return typed === undefined ? String(shelf.get(v.variantId)?.onHand ?? 0) : typed
  }

  // What would actually be sent: the lines whose number differs from the shelf.
  // Computed here as well as skipped server-side, because the button has to be
  // able to say how many rows it is about to move BEFORE the press.
  const changed = variants
    .map((v) => ({ v, next: Number(shown(v)), now: shelf.get(v.variantId)?.onHand ?? 0 }))
    .filter((x) => Number.isFinite(x.next) && x.next !== x.now)

  const refusals = variants
    .map((v) => ({ v, why: stockRefusal(Number(shown(v)), shelf.get(v.variantId)?.held ?? 0) }))
    .filter((x) => x.why !== null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!roomId || changed.length === 0) return
    setBusy(true)
    onError(null)
    try {
      const written = await setStockBatch(
        roomId,
        changed.map((x) => ({ variantId: x.v.variantId, onHand: x.next })),
      )
      onSaved(written)
    } catch (err) {
      onError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  if (mine.length === 0) {
    return (
      <p className="empty">
        You do not run a stock room, so there is nowhere to put anything. A shop
        administrator adds you to one.
      </p>
    )
  }

  return (
    <form className="addstock" onSubmit={submit}>
      {error && <p className="roles__error" role="alert">{error}</p>}

      {/* STEP ONE. Every number below is a property of a variant AND a room. */}
      <label className="admin__field">
        <span>Which store</span>
        <select
          value={roomId ?? ''}
          onChange={(e) => {
            onPickRoom(e.target.value || null)
            // The item is dropped with the room, deliberately: the counts on
            // screen belong to the old shelf, and carrying them across is how
            // somebody saves Burnaby's numbers onto Lima's.
            onPickItem('')
            setCounts({})
          }}
        >
          <option value="">Choose a store</option>
          {mine.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} — {r.city}
            </option>
          ))}
        </select>
      </label>

      {!roomId && (
        <p className="admin__hint">
          The store comes first because everything else depends on it — what is
          there now, what is already promised, and what has run out.
        </p>
      )}

      {roomId && loading && <p className="empty" aria-busy="true">Reading that shelf…</p>}

      {roomId && !loading && !item && (
        <div className="addstock__pick">
          {/* GROUPED BY WHY (REC-65). The heading is the store, not "Needs
              attention": "Recently counted here" is a shortcut and never a
              reason, and putting it under "attention" is how an item that
              needed nothing looked as though it did. Each row says which
              combinations, so nobody opens an item to learn which three ran
              out. The whole row is the button. */}
          {groups.length > 0 && (
            <div className="addstock__suggest">
              <h4 className="shopitems__subhead">At {room?.name ?? 'this store'}</h4>
              {groups.map((g) => (
                <section key={g.reason} className={`addstock__group addstock__group--${g.reason}`}>
                  <h5 className="addstock__grouphead">{g.heading}</h5>
                  <ul className="addstock__list">
                    {g.items.map((s) => (
                      <li key={s.itemId}>
                        <button
                          type="button"
                          className="addstock__row"
                          onClick={() => {
                            onPickItem(s.itemId)
                            setCounts({})
                          }}
                        >
                          <span className="addstock__name">{s.itemName}</span>
                          <span className="addstock__why">{s.detail}</span>
                          {s.combos && <span className="addstock__combos">{s.combos}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {g.more > 0 && (
                    <p className="admin__hint">
                      {g.more} more {g.more === 1 ? 'item' : 'items'} — search below.
                    </p>
                  )}
                </section>
              ))}
            </div>
          )}

          <label className="admin__field addstock__search">
            <span>
              Or find an item{' '}
              {/* Stated, because a shortcut nobody is told about is a shortcut
                  nobody uses — and this one is worth telling people about
                  precisely because it overrides the browser's own. */}
              <kbd className="addstock__kbd">Ctrl</kbd>
              <kbd className="addstock__kbd">K</kbd>
            </span>
            <input
              ref={searchRef}
              type="search"
              value={query}
              placeholder="Start typing a name or a code"
              autoComplete="off"
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>

          {matches.length === 0 ? (
            <p className="admin__hint">
              {/* An empty catalogue and a query that found nothing are different
                  facts, and "Nothing matches “”" is the shape of sentence that
                  makes a screen look broken. */}
              {query.trim() ? `Nothing matches “${query}”. ` : 'Nothing to stock yet. '}
              A delisted item is not offered — relist it on Items first, or
              create one.
            </p>
          ) : (
            <ul className="addstock__results">
              {matches.map((i) => (
                <li key={i.itemId}>
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => {
                      onPickItem(i.itemId)
                      setCounts({})
                    }}
                  >
                    {i.name}
                    <span className="shoprooms__who">{i.code}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* STEP THREE. Every combination, one press. */}
      {roomId && item && (
        <div className="addstock__grid">
          <h4 className="shopitems__subhead">
            {item.name}
            <span className="shopitems__scope">
              {' · '}{room?.name ?? ''}
            </span>
            <button
              type="button"
              className="btn btn--xs"
              onClick={() => {
                onPickItem('')
                setCounts({})
              }}
            >
              Change item
            </button>
          </h4>

          {variants.length === 0 ? (
            <p className="empty">
              This item has no combinations, so there is nowhere for stock to
              live. Give it at least one on Items — even a mug needs one.
            </p>
          ) : (
            <table className="admin__table addstock__table">
              <thead>
                <tr>
                  <th scope="col">Combination</th>
                  <th scope="col">There now</th>
                  <th scope="col">Assigned</th>
                  <th scope="col">Count</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => {
                  const row = shelf.get(v.variantId)
                  const now = row?.onHand ?? 0
                  const held = row?.held ?? 0
                  const why = stockRefusal(Number(shown(v)), held)
                  return (
                    <tr key={v.variantId}>
                      <th scope="row">{v.label}</th>
                      {/* Shown, not remembered. A "type the new total" form
                          without the old total beside it is a subtraction done
                          from memory. */}
                      <td>{now}</td>
                      <td>{held}</td>
                      <td>
                        <input
                          className="shopitems__count"
                          type="number"
                          min={0}
                          value={shown(v)}
                          disabled={busy}
                          aria-label={`${item.name} ${v.label} on the shelf`}
                          onChange={(e) =>
                            setCounts((c) => ({ ...c, [v.variantId]: e.target.value }))
                          }
                        />
                        {why && <span className="shopitems__refusal">{why}</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          <p className="admin__hint">
            <strong>Type the new total, not what you are adding.</strong> What is
            recorded is what you can see on the shelf, so two people counting the
            same boxes cannot produce a number neither of them saw. Nothing is
            saved until you press once, and a refusal on any line saves none of
            it.
          </p>
        </div>
      )}

      <div className="shopitems__actions">
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={busy || !roomId || !item || changed.length === 0 || refusals.length > 0}
        >
          {busy
            ? 'Saving…'
            : changed.length === 0
              ? 'Nothing changed'
              : `Save ${changed.length} ${changed.length === 1 ? 'count' : 'counts'}`}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}
